import React, { useState, useMemo } from "react";
import { Card, OverlayTrigger, Popover, Button } from "react-bootstrap";
import { HiArrowUpRight } from "react-icons/hi2";
import "./News.css";

/** Split on sentence boundaries and light clause breaks so pairs are not one giant block. */
function splitIntoSentences(text) {
  if (text == null || String(text).trim() === "") return [];
  const s = String(text).trim().replace(/\s+/g, " ");
  const pieces = s
    .split(/(?<=[.!?…。])\s+|;\s+|:\s+|：\s+|—\s+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return pieces.length ? pieces : [s];
}

/** Pair original and translated text sentence-by-sentence for hover alignment. */
function zipSentencePairs(originalFull, translatedFull) {
  const orig = splitIntoSentences(originalFull);
  const trans = splitIntoSentences(translatedFull);
  const n = Math.max(orig.length, trans.length);
  const pairs = [];
  for (let i = 0; i < n; i++) {
    const o = orig[i] ?? "";
    const t = trans[i] ?? "";
    if (o.trim() || t.trim()) pairs.push({ original: o, translated: t });
  }
  return pairs;
}

/**
 * Server / Firestore store aligned chunks (index i ↔ index i).
 */
function pairsFromAlignedChunks(originalChunks, translatedChunks) {
  const o = originalChunks || [];
  const t = translatedChunks || [];
  const n = Math.min(o.length, t.length);
  if (n === 0) return null;
  if (o.length !== t.length) {
    console.warn("NewsCard: chunk array length mismatch", o.length, t.length);
  }
  const pairs = [];
  for (let i = 0; i < n; i++) {
    const original = String(o[i] ?? "").trim();
    const translated = String(t[i] ?? "").trim();
    if (original || translated) pairs.push({ original, translated });
  }
  return pairs.length ? pairs : null;
}

function pairsWithFallback(originalChunks, translatedChunks) {
  const aligned = pairsFromAlignedChunks(originalChunks, translatedChunks);
  if (aligned?.length > 1) {
    // Each chunk was translated separately — index i ↔ index i.
    return aligned;
  }
  const original = (originalChunks || []).join(" ").trim();
  const translated = (translatedChunks || []).join(" ").trim();
  // Legacy single-block text: pair by sentence only (no proportional word guesswork).
  return zipSentencePairs(original, translated);
}

const NewsCard = ({ news, selectedLanguage, translationData }) => {
  const [hoveredTitleChunks, setHoveredTitleChunks] = useState({});
  const [hoveredSummaryChunks, setHoveredSummaryChunks] = useState({});

  const titlePairs = useMemo(
    () =>
      pairsWithFallback(
        translationData.originalTitleChunks,
        translationData.translatedTitleChunks
      ),
    [translationData]
  );

  const summaryPairs = useMemo(
    () =>
      pairsWithFallback(
        translationData.originalSummaryChunks,
        translationData.translatedSummaryChunks
      ),
    [translationData]
  );

  const renderSentencePairs = (pairs, hoveredChunks, setHoveredChunks, idPrefix) => {
    return pairs.map((pair, chunkIndex) => {
      const originalChunk = pair.original;
      const translatedChunk = pair.translated;
      const chunkStyle = {
        cursor: "pointer",
        padding: "0 1px",
        backgroundColor: hoveredChunks[chunkIndex] ? "yellow" : "transparent",
        transition: "background-color 0.3s ease",
      };
      return (
        <React.Fragment key={`${idPrefix}-${chunkIndex}`}>
          {chunkIndex > 0 ? " " : null}
          <OverlayTrigger
            placement="top"
            overlay={
              <Popover id={`popover-${idPrefix}-${chunkIndex}`}>
                <Popover.Body
                  style={{ maxWidth: 360, whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {originalChunk || "—"}
                </Popover.Body>
              </Popover>
            }
          >
            <span
              style={chunkStyle}
              onMouseEnter={() => setHoveredChunks({ [chunkIndex]: true })}
              onMouseLeave={() => setHoveredChunks({})}
            >
              {translatedChunk.replace(/\s+/g, " ")}
            </span>
          </OverlayTrigger>
        </React.Fragment>
      );
    });
  };

  if (!translationData) {
    return null;
  }

  return (
    <Card>
      <Card.Body>
        <div className="news-card-title-row">
          <Card.Title className="news-card-title">
            {renderSentencePairs(
              titlePairs,
              hoveredTitleChunks,
              setHoveredTitleChunks,
              "title"
            )}
          </Card.Title>
          <Button
            as="a"
            href={news.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline-secondary"
            size="sm"
            className="news-outline-btn news-card-visit-link"
            aria-label="Visit article"
          >
            visit
            <HiArrowUpRight className="news-card-visit-icon" aria-hidden />
          </Button>
        </div>
        <Card.Text style={{ whiteSpace: "normal" }}>
          {renderSentencePairs(
            summaryPairs,
            hoveredSummaryChunks,
            setHoveredSummaryChunks,
            "summary"
          )}
        </Card.Text>
      </Card.Body>
    </Card>
  );
};

export default NewsCard;
